import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { useMatters } from '@/contexts/MatterContext';
import type { MatterDetail } from '@/lib/types';

// P4 Section 4.3 / 4.6 / 4.7. Global overlays for the hearing screen:
//   - Cmd/Ctrl+M  matter switcher (fuzzy, recently used first, no reload)
//   - ? or Cmd/Ctrl+/  keyboard shortcuts cheat sheet
//   - header "?" icon  in-app help drawer (dispatches cc:open-help)
// Owns all overlay state + the global key handler. Mounted once in HearingShell.

type Overlay = 'none' | 'switcher' | 'shortcuts' | 'help';

const RECENT_KEY = 'cc_recent_matters';

function readRecents(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}
function pushRecent(id: string): void {
  try {
    const next = [id, ...readRecents().filter((x) => x !== id)].slice(0, 8);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.isContentEditable
  );
}

export function CommandOverlays() {
  const [overlay, setOverlay] = useState<Overlay>('none');

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'm' || e.key === 'M')) {
        e.preventDefault();
        setOverlay((o) => (o === 'switcher' ? 'none' : 'switcher'));
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setOverlay((o) => (o === 'shortcuts' ? 'none' : 'shortcuts'));
        return;
      }
      if (e.key === '?' && !isEditable(e.target)) {
        e.preventDefault();
        setOverlay((o) => (o === 'shortcuts' ? 'none' : 'shortcuts'));
        return;
      }
      if (e.key === 'Escape') {
        setOverlay('none');
      }
    }
    const openHelp = (): void => setOverlay('help');
    const openShortcuts = (): void => setOverlay('shortcuts');
    const openSwitcher = (): void => setOverlay('switcher');
    window.addEventListener('keydown', onKey);
    window.addEventListener('cc:open-help', openHelp);
    window.addEventListener('cc:open-shortcuts', openShortcuts);
    window.addEventListener('cc:open-switcher', openSwitcher);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('cc:open-help', openHelp);
      window.removeEventListener('cc:open-shortcuts', openShortcuts);
      window.removeEventListener('cc:open-switcher', openSwitcher);
    };
  }, []);

  const close = useCallback(() => setOverlay('none'), []);

  return (
    <>
      {overlay === 'switcher' ? <MatterSwitcher onClose={close} /> : null}
      {overlay === 'shortcuts' ? <ShortcutsModal onClose={close} /> : null}
      {overlay === 'help' ? <HelpDrawer onClose={close} /> : null}
    </>
  );
}

// --- matter switcher --------------------------------------------------------

function MatterSwitcher({ onClose }: { onClose: () => void }) {
  const { matters, activeMatterId, setActiveMatterId } = useMatters();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const ordered = useMemo(() => orderMatters(matters), [matters]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ordered;
    return ordered.filter((m) =>
      [m.name, m.reference, m.court].filter(Boolean).join(' ').toLowerCase().includes(q),
    );
  }, [ordered, query]);

  function choose(m: MatterDetail): void {
    pushRecent(m.id);
    setActiveMatterId(m.id);
    onClose();
  }

  return (
    <Backdrop onClose={onClose} align="top">
      <div
        className="w-full max-w-lg overflow-hidden rounded-lg border border-stone-700 bg-stone-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Switch matter"
      >
        <div className="flex items-center gap-2 border-b border-stone-800 px-3 py-2.5">
          <Search className="h-4 w-4 text-stone-500" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setCursor(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setCursor((c) => Math.min(c + 1, filtered.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setCursor((c) => Math.max(c - 1, 0));
              } else if (e.key === 'Enter' && filtered[cursor]) {
                choose(filtered[cursor]);
              }
            }}
            placeholder="Switch matter..."
            className="flex-1 bg-transparent text-sm text-stone-100 placeholder:text-stone-600 focus:outline-none"
          />
          <kbd className="rounded bg-stone-800 px-1.5 py-0.5 text-[10px] text-stone-500">Esc</kbd>
        </div>
        <ul className="max-h-80 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <li className="px-3 py-3 text-sm text-stone-500">No matters match.</li>
          ) : (
            filtered.map((m, i) => (
              <li key={m.id}>
                <button
                  type="button"
                  onMouseEnter={() => setCursor(i)}
                  onClick={() => choose(m)}
                  className={
                    'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm ' +
                    (i === cursor ? 'bg-amber-950/30 text-amber-200' : 'text-stone-300')
                  }
                >
                  <span className="min-w-0 flex-1 truncate">
                    {m.name}
                    {m.reference ? (
                      <span className="ml-2 text-[11px] text-stone-500">{m.reference}</span>
                    ) : null}
                  </span>
                  {m.id === activeMatterId ? (
                    <span className="text-[10px] uppercase tracking-[0.15em] text-amber-500">
                      Active
                    </span>
                  ) : null}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </Backdrop>
  );
}

function orderMatters(matters: MatterDetail[]): MatterDetail[] {
  const recents = readRecents();
  const rank = (id: string): number => {
    const i = recents.indexOf(id);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  return [...matters].sort((a, b) => rank(a.id) - rank(b.id));
}

// --- shortcuts cheat sheet --------------------------------------------------

const SHORTCUTS: Array<{ keys: string; label: string }> = [
  { keys: 'F1 – F4', label: 'Fast actions: counter, find evidence, spot weakness, next move' },
  { keys: 'Shift+F1 – F4', label: 'Deep versions (search Dropbox + Outlook + AustLII)' },
  { keys: 'F5', label: 'Start / stop listening' },
  { keys: 'F6', label: 'Clear the transcript' },
  { keys: 'Ctrl+M', label: 'Switch matter' },
  { keys: 'Ctrl+/  or  ?', label: 'This shortcuts sheet' },
];

function ShortcutsModal({ onClose }: { onClose: () => void }) {
  return (
    <Backdrop onClose={onClose} align="center">
      <div
        className="w-full max-w-md rounded-lg border border-stone-700 bg-stone-900 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Keyboard shortcuts"
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm uppercase tracking-[0.2em] text-stone-300">Keyboard shortcuts</h2>
          <CloseButton onClose={onClose} />
        </div>
        <dl className="space-y-2">
          {SHORTCUTS.map((s) => (
            <div key={s.keys} className="flex items-start justify-between gap-4">
              <dt className="shrink-0">
                <kbd className="rounded bg-stone-800 px-2 py-0.5 text-xs text-amber-300">{s.keys}</kbd>
              </dt>
              <dd className="text-right text-sm text-stone-400">{s.label}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Backdrop>
  );
}

// --- help drawer ------------------------------------------------------------

const HELP_TOPICS: Array<{ title: string; body: string }> = [
  {
    title: 'Fast vs deep mode',
    body: 'Fast (F1-F4) answers in seconds with no tools, for the live exchange. Deep (Shift+F1-F4) searches your Dropbox, Outlook, and AustLII; use it in a break or a long examination. Every response card shows a FAST or DEEP badge.',
  },
  {
    title: 'Live classifier',
    body: 'While listening, the glowing chips above the buttons are the AI reading the moment and suggesting what to have ready. Tap one to run it. They are nudges, not instructions.',
  },
  {
    title: 'Hearing prep',
    body: 'Open a matter and click Prep to generate a pre-hearing brief the night before: the one thing to land, your strongest arguments, what they will argue, documents to have open, and your opening words.',
  },
  {
    title: 'Courtroom protocol',
    body: 'Before the first Start Listening of a session you confirm the lawful-use checklist (leave, screen privacy, local transcription, duty of candour). Verify every citation and fact before relying on it.',
  },
  {
    title: 'Response voice',
    body: 'Plain English is the default: every legal term translated, short sentences, what to say next. Switch to Pro in Settings for the senior-solicitor register.',
  },
];

function HelpDrawer({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState('');
  const topics = HELP_TOPICS.filter(
    (t) =>
      !query.trim() ||
      (t.title + ' ' + t.body).toLowerCase().includes(query.trim().toLowerCase()),
  );
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose} role="presentation">
      <aside
        className="flex h-full w-full max-w-sm flex-col border-l border-stone-800 bg-stone-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Help"
      >
        <div className="flex items-center justify-between border-b border-stone-800 px-4 py-3">
          <h2 className="text-sm uppercase tracking-[0.2em] text-stone-300">Help</h2>
          <CloseButton onClose={onClose} />
        </div>
        <div className="border-b border-stone-800 px-4 py-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search help..."
            className="w-full rounded-md border border-stone-800 bg-stone-950 px-3 py-2 text-sm text-stone-200 placeholder:text-stone-600 focus:border-amber-700/60 focus:outline-none"
          />
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {topics.map((t) => (
            <div key={t.title}>
              <h3 className="text-sm font-medium text-amber-400">{t.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-stone-400">{t.body}</p>
            </div>
          ))}
          {topics.length === 0 ? <p className="text-sm text-stone-500">No help topic matches.</p> : null}
        </div>
      </aside>
    </div>
  );
}

// --- shared bits ------------------------------------------------------------

function Backdrop({
  children,
  onClose,
  align,
}: {
  children: React.ReactNode;
  onClose: () => void;
  align: 'top' | 'center';
}) {
  return (
    <div
      className={
        'fixed inset-0 z-50 flex justify-center bg-black/50 p-4 ' +
        (align === 'top' ? 'items-start pt-24' : 'items-center')
      }
      onClick={onClose}
      role="presentation"
    >
      {children}
    </div>
  );
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      className="rounded p-1 text-stone-500 hover:text-stone-200"
    >
      <X className="h-4 w-4" />
    </button>
  );
}
